import { useCallback, useLayoutEffect, useState } from "react";

import { useResizeObserver } from "@/utils/useResizeObserver";

import type { NodePoints, ViewBox } from "./types";
import type { NodeModel } from "@/models/nodes/types";
import type { RefObject } from "react";

type BorderRadius = number | { tlr: number; trr: number; blr: number; brr: number };

/**
 * Calculate points of node so we can draw with svg by using them
 * We move all of them to the center by 1px so we can compensate 0.5px that cutted by svg border
 */
function calculatePoints(width: number, height: number, borderRadius: BorderRadius): NodePoints {
  const br =
    typeof borderRadius === "object"
      ? borderRadius
      : { tlr: borderRadius, trr: borderRadius, blr: borderRadius, brr: borderRadius };
  const calculateCorner = (value: number) => {
    return value - value / Math.sqrt(2);
  };

  return {
    tl: { x: br.tlr + 1, y: 1 },
    lt: { x: 1, y: br.tlr + 1 },
    tlc: { x: calculateCorner(br.tlr) + 1, y: calculateCorner(br.tlr) + 1 },
    tla: { x: 1, y: 1 },

    tr: { x: width - br.trr - 1, y: 1 },
    rt: { x: width - 1, y: br.trr + 1 },
    trc: { x: width - calculateCorner(br.trr) - 1, y: calculateCorner(br.trr) + 1 },
    tra: { x: width - 1, y: 1 },

    rb: { x: width - 1, y: height - br.brr - 1 },
    br: { x: width - br.brr - 1, y: height - 1 },
    brc: { x: width - calculateCorner(br.brr) - 1, y: height - calculateCorner(br.brr) - 1 },
    bra: { x: width - 1, y: height - 1 },

    lb: { x: 1, y: height - br.blr },
    bl: { x: br.blr + 1, y: height - 1 },
    blc: { x: calculateCorner(br.blr) + 1, y: height - calculateCorner(br.blr) - 1 },
    bla: { x: 1, y: height - 1 },

    tc: { x: width / 2 + 1, y: 1 },
    bc: { x: width / 2 + 1, y: height - 1 },
    lc: { x: 1, y: height / 2 },
    rc: { x: width - 1, y: height / 2 },
  };
}

/**
 * This hook accepts ref of node and node itself
 * and it calculates absolute points of node in px
 *
 * @param ref - react ref
 * @param node - node from node tree
 * @returns calculated points
 */
export function useNodePoints(ref: RefObject<HTMLDivElement | null>, node?: NodeModel) {
  const [viewBox, setViewBox] = useState<ViewBox>({
    width: 0,
    height: 0,
  });
  const [sizes, setSizes] = useState<DOMRect>();
  const [nodePoints, setNodePoints] = useState<NodePoints>({
    tl: { x: 0, y: 0 },
    lt: { x: 0, y: 0 },
    tlc: { x: 0, y: 0 },
    tla: { x: 0, y: 0 },
    tr: { x: 0, y: 0 },
    rt: { x: 0, y: 0 },
    trc: { x: 0, y: 0 },
    tra: { x: 0, y: 0 },
    rb: { x: 0, y: 0 },
    br: { x: 0, y: 0 },
    brc: { x: 0, y: 0 },
    bra: { x: 0, y: 0 },
    lb: { x: 0, y: 0 },
    bl: { x: 0, y: 0 },
    blc: { x: 0, y: 0 },
    bla: { x: 0, y: 0 },
    tc: { x: 0, y: 0 },
    bc: { x: 0, y: 0 },
    lc: { x: 0, y: 0 },
    rc: { x: 0, y: 0 },
  });

  const calculate = useCallback(
    (target: Element) => {
      const { width, height } = target.getBoundingClientRect();
      const {
        borderTopLeftRadius = "",
        borderTopRightRadius = "",
        borderBottomLeftRadius = "",
        borderBottomRightRadius = "",
      } = ref.current?.style || {};
      const br = {
        tlr: Number.parseInt(borderTopLeftRadius, 10) || 0,
        trr: Number.parseInt(borderTopRightRadius, 10) || 0,
        blr: Number.parseInt(borderBottomLeftRadius, 10) || 0,
        brr: Number.parseInt(borderBottomRightRadius, 10) || 0,
      };
      const points = calculatePoints(width, height, br);
      setViewBox({ width, height });
      setNodePoints(points);
    },
    [ref],
  );

  useResizeObserver(ref, (entry) => {
    setSizes(entry.contentRect);
  });

  useLayoutEffect(() => {
    if (!ref.current) {
      return;
    }
    calculate(ref.current);
    queueMicrotask(() => {
      if (ref.current) {
        calculate(ref.current);
      }
    });
  }, [ref, sizes, node?.descendants, calculate]);

  return { nodePoints, viewBox };
}
